<script setup lang="ts">
import { ElIcon } from 'element-plus'
import { computed, inject } from 'vue'

import {
  BoxComponent,
  ButtonComponent,
  FlexComponent,
  ImageComponent,
  PlatformHandle,
  ThumbnailOnlyBadge,
  TypographyComponent,
} from '../..'
import LoadingIcon from '../../../assets/icons/loading.svg?component'
import PlayIcon from '../../../assets/icons/play.svg?component'
import ThumbsDownIcon from '../../../assets/icons/thumbs-down.svg?component'
import ThumbsUpIcon from '../../../assets/icons/thumbs-up.svg?component'
import { formatterProviderKey } from '../../../constants'
import { defaultFormatter } from '../../../formatters'
import { type ContentItemPreview, ContentItemStatusActionEnum, ContentItemStatusEnum } from '../../../types'
import { isThumbnailOnlyVideo, platformOfContentType } from '../../../utils'
import { CONTENT_ITEM_MEDIA_ASPECT_RATIO } from '../../constants/content-item'

type ViewMode = 'admin' | 'advertiser'

export type ContentItemProps = {
  item: ContentItemPreview
  isLoading?: boolean
  viewMode: ViewMode
}

const props = defineProps<ContentItemProps>()
const emit = defineEmits<{
  (event: 'update:status', payload: { id: string; action: ContentItemStatusActionEnum }): void
  (event: 'onOpen', id: string): void
}>()

const primaryAsset = props.item.assets[0]!
const isThumbnailOnly = isThumbnailOnlyVideo(primaryAsset)
const platform = computed(() => platformOfContentType(props.item.type))

const handleAction = (action: ContentItemStatusActionEnum, isActive: boolean) => {
  if (isActive) {
    return
  }
  emit('update:status', { id: props.item.id, action })
}

const handleOpen = () => {
  emit('onOpen', props.item.id)
}

const STATUS_CONFIG: Partial<Record<ContentItemStatusEnum, { label: string; icon: unknown; color: string }>> = {
  [ContentItemStatusEnum.Approved]: { label: 'Approved', icon: ThumbsUpIcon, color: 'var(--success)' },
  [ContentItemStatusEnum.Rejected]: { label: 'Rejected', icon: ThumbsDownIcon, color: 'var(--error)' },
}

const statusBadge = computed(() => {
  const status = props.item.status as ContentItemStatusEnum
  const config = STATUS_CONFIG[status]
  if (!config) {
    return null
  }
  return {
    label: config.label,
    icon: props.isLoading ? LoadingIcon : config.icon,
    color: config.color,
  }
})

const actionButtons = computed(() => {
  const status = props.item.status as ContentItemStatusEnum
  const isApproved = status === ContentItemStatusEnum.Approved
  const isRejected = status === ContentItemStatusEnum.Rejected
  return [
    {
      action: ContentItemStatusActionEnum.Approve,
      icon: ThumbsUpIcon,
      label: isApproved ? 'Approved' : 'Approve',
      isActive: isApproved,
      variant: isApproved ? ('success' as const) : ('primary' as const),
      cssClass: 'content-item__action-button--approve',
    },
    {
      action: ContentItemStatusActionEnum.Reject,
      icon: ThumbsDownIcon,
      label: isRejected ? 'Rejected' : 'Reject',
      isActive: isRejected,
      variant: isRejected ? ('danger' as const) : ('primary' as const),
      cssClass: 'content-item__action-button--reject',
    },
  ]
})

const { formatNumber } = inject(formatterProviderKey, defaultFormatter)

const mediaAspectRatio = `${CONTENT_ITEM_MEDIA_ASPECT_RATIO}`
</script>
<template>
  <BoxComponent class="content-item" @click="handleOpen">
    <BoxComponent class="content-item__media">
      <ImageComponent
        v-if="primaryAsset.type === 'image'"
        :image="primaryAsset"
        layout="fullWidth"
        width="100%"
        height="100%"
        :alt="item.profileHandle || 'Content item'"
        class="content-item__image"
      />
      <img
        v-else-if="primaryAsset.thumbnailUrl"
        :src="primaryAsset.thumbnailUrl"
        :alt="item.profileHandle || 'Content item'"
        class="content-item__image"
      />
      <video v-else-if="!isThumbnailOnly" :src="primaryAsset.url" class="content-item__video" />
      <BoxComponent
        v-if="viewMode === 'admin' && statusBadge"
        :p="8"
        class="content-item__status-badge"
        :style="{ background: statusBadge.color }"
        role="img"
        :aria-label="statusBadge.label"
        :title="statusBadge.label"
      >
        <ElIcon :size="20" class="content-item__status-icon" aria-hidden="true">
          <component :is="statusBadge.icon" />
        </ElIcon>
      </BoxComponent>
      <FlexComponent
        v-if="viewMode === 'admin'"
        class="content-item__actions-overlay"
        direction="row"
        justify="center"
        align="center"
        :gap="8"
        @click.stop
      >
        <ButtonComponent
          v-for="button in actionButtons"
          :key="button.action"
          :variant="button.variant"
          :is-disabled="isLoading"
          :aria-pressed="button.isActive"
          :class="['content-item__action-button', button.cssClass, { 'content-item__action-button--active': button.isActive }]"
          @click="handleAction(button.action, button.isActive)"
        >
          <template #iconLeft>
            <ElIcon :size="18">
              <component :is="button.icon" />
            </ElIcon>
          </template>
          {{ button.label }}
        </ButtonComponent>
      </FlexComponent>
      <ThumbnailOnlyBadge v-if="isThumbnailOnly" size="small" class="content-item__thumbnail-only-badge" />
      <ElIcon v-else-if="primaryAsset.type === 'video'" class="content-item__play-icon" :size="24">
        <PlayIcon />
      </ElIcon>
    </BoxComponent>
    <BoxComponent :p="16" class="content-item__footer">
      <FlexComponent align="center" :gap="2" justify="space-between">
        <BoxComponent class="content-item__footer-source">
          <PlatformHandle
            :handle="item.profileHandle"
            :platform="platform"
            :is-linked="false"
            variant="body-small"
            class="content-item__footer-user"
          />
          <TypographyComponent
            v-if="viewMode === 'admin' && item.advertiser?.name"
            variant="body-small"
            text-truncate
            class="content-item__advertiser-name"
            :title="item.advertiser?.name"
          >
            {{ item.advertiser?.name }}
          </TypographyComponent>
        </BoxComponent>
        <TypographyComponent v-if="item.numberOfClicks !== undefined" variant="title-medium" text-truncate white-space="nowrap">
          {{ formatNumber(item.numberOfClicks) }} clicks
        </TypographyComponent>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>
</template>
<style scoped lang="scss">
.content-item {
  background: var(--inverted);
  border: 1px solid var(--button-hover);
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;

  &__media {
    aspect-ratio: v-bind(mediaAspectRatio);
    position: relative;
    width: 100%;
    overflow: hidden;
  }

  &__image,
  &__video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &__status-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    border: 2px solid var(--inverted);
  }

  &__actions-overlay {
    position: absolute;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 8px;
    opacity: 0;
    pointer-events: none;
    background-color: var(--primary);
    transition: opacity var(--transition) ease;
    border-radius: 16px;
  }

  &__action-button.button {
    padding: 8px 12px;
    gap: 4px;
    border-radius: 8px;

    &:not(.content-item__action-button--active):not(.disabled):hover {
      background: var(--navigation-background);
    }
  }

  &__action-button--active.button {
    cursor: default;
  }

  &__thumbnail-only-badge {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;
  }

  &__play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 48px;
    height: 48px;
    transform: translate(-50%, -50%);
    border-radius: 40px;
    background: rgba(34, 34, 34, 0.8);
  }

  &:hover &__actions-overlay {
    opacity: 1;
    pointer-events: all;
  }

  &__footer-source {
    min-width: 0;
  }

  &__footer-user {
    margin-bottom: 2px;
  }

  &__advertiser-name {
    display: block;
    max-width: 20ch;
  }
}
</style>
